import { defineStore } from "pinia";
import Services from '@/services/sutting/settingServices';
import { mapSetting } from '@/helpers/mapFuncs';
import { usePoliceStore } from '../police';
import { useAdmissionChannelsStore } from './admissionChannels';
import { useAlmelakatStore } from './almelakats';
import { useBodyMainPartsStore } from './_bodyMainParts';
import { useBodySideStore } from './bodySide';
import { useBodySubPartsStore } from './bodySubParts';
import { useCategoriesStore } from './categories';
import { useCollegesStore } from './colleges';
import { useDepartmentsStore } from './departments';
import { useDistrictsStore } from './districts';
import { useEducationalInstitutionsStore } from './educationalInstitutions';
import { useFileNamesStore } from './fileNames';
import { useGovernorateStore } from './governorate';
import { useHousingTypeStore } from './housingType';
import { useInstitutionsStore } from './institutions';
import { useIssuersNewIdentificationCardsStore } from './issuersNewIdentificationCards';
import { useIssuersOldIdentificationCardsStore } from './issuersOldIdentificationCards';
import { useIssuersPassportsStore } from './issuersPassports';
import { useIssuersResidentialCardsStore } from './issuersResidentialCards';
import { useLevelOneStore } from './levelOne';
import { useLevelTwoStore } from './levelTwo';
import { useLevelThreeStore } from './levelThree';
import { useOwnershipTypesStore } from './ownershipTypes';
import { usePersonTypesStore } from './personTypes';
import { usePlaceAffiliationsStore } from './placeAffiliations';
import { usePositionsStore } from './positions';
import { useRegionsStore } from './regions';
import { useSkillsStore } from './skills';
import { useTownshipsStore } from './townships';
import { useTypeInjuriesStore } from './typeInjuries';

const lookupStores = {
    skills: useSkillsStore,
    educationalInstitutions: useEducationalInstitutionsStore,
    colleges: useCollegesStore,
    departments: useDepartmentsStore,
    categories: useCategoriesStore,
    personTypes: usePersonTypesStore,
    admissionChannels: useAdmissionChannelsStore,
    almelakat: useAlmelakatStore,
    bodySide: useBodySideStore,
    bodyMainParts: useBodyMainPartsStore,
    bodySubParts: useBodySubPartsStore,
    housingType: useHousingTypeStore,
    institutions: useInstitutionsStore,
    issuersNewIdentificationCards: useIssuersNewIdentificationCardsStore,
    issuersOldIdentificationCards: useIssuersOldIdentificationCardsStore,
    issuersPassports: useIssuersPassportsStore,
    issuersResidentialCards: useIssuersResidentialCardsStore,
    levelOne: useLevelOneStore,
    levelTwo: useLevelTwoStore,
    levelThree: useLevelThreeStore,
    ownershipTypes: useOwnershipTypesStore,
    placeAffiliations: usePlaceAffiliationsStore,
    typesInjuries: useTypeInjuriesStore,
    regions: useRegionsStore,
    townships: useTownshipsStore,
    districts: useDistrictsStore,
    governorates: useGovernorateStore,
    positions: usePositionsStore,
    fileNames: useFileNamesStore,
    policeField: usePoliceStore,
};

// استخراج اسم جدول اللوكاب من مفتاح الإعداد
function getLookupName(setting) {
    return setting.settingKey?.match(/^lookup\.(.+)\.version$/)?.[1];
}

let refreshTimer = null;

export const useSettingsStore = defineStore("settings", {
persist: true,
state:()=>({
    settings: [],
}),
getters : {
    getSettings() {
        return this.settings.map(mapSetting)
    },
    getSettingsByCategory(category) {
        return this.settings.filter(s => s.category === category).map(mapSetting)
    },
},
actions : {
    // تحديث بيانات الإعدادات في المتجر
    setSettings(settings) {
        this.settings = settings.reduce((acc, item) => {
            if (!acc[item.category]) {
                acc[item.category] = [];
            }

            acc[item.category].push(item);

            return acc;
        }, {});
    },

    setSettingsByCategory(category, settings) {
        if(!this.settings[category]) {
            this.settings[category] = [];
        }
        this.settings[category] = settings;
    },

    getAll(data) {
        return Services.getAll(data)
        .then((response)=>{
            this.setSettings(response.data.data??[])
            return response
        })
    },

    // جلب الإعدادات حسب التصنيف
    getByCategory(category, data) {
        return Services.getByCategory(category, data)
    },

    syncLookupTables(){
        //بناءً على الإصدارات الجديدة، نقوم بتحديث الجداول التي تغيرت فقط
        let paramsGetAll =  {
            page: 0,
            size: 9999999,
        };

        return this.getByCategory('Lookup', paramsGetAll)
        .then((response)=>{
            
            let lookupVersions = response.data.data??[];

            //مقارنة الإصدارات الجديدة مع الإصدارات المخزنة في المتجر
            const changedLookups = lookupVersions.filter((setting) => {
                let currentVersion = Number(this.settings?.Lookup?.find((x)=>x.settingKey == setting.settingKey)?.settingValue) ?? 0
                let newVersion = Number(setting.settingValue) ?? 0

                return currentVersion < newVersion;
            });                

            const updates = changedLookups.map((setting) => {
                const store = lookupStores[getLookupName(setting)];
                return store ? store().getAll(paramsGetAll) : Promise.resolve();
            });

            return Promise.all(updates).then(() => {
                // بعد تحديث الجداول، نقوم بتحديث بيانات الإعدادات في المتجر
                this.setSettingsByCategory('Lookup', lookupVersions);
            });
            
        })
    },

    // بدء الفحص الأول ثم تكراره كل خمس دقائق
    startLookupRefresh(interval = 5 * 60 * 1000) {
        console.log('Starting lookup refresh with interval:', interval);
        if (refreshTimer) clearInterval(refreshTimer);

        refreshTimer = setInterval(() => {
            this.syncLookupTables().catch((error) => console.error('Lookup refresh failed', error));
        }, interval);
    },

    // إيقاف المؤقت عند الحاجة
    stopLookupRefresh() {
        if (refreshTimer) clearInterval(refreshTimer);
        refreshTimer = null;
    },
}
});
